/**
 * Photographs of a unit: add them, order them, remove them.
 *
 * **The order is the whole point.** The first photograph is the one the listing leads with — on the
 * card, in the WhatsApp preview, at the top of the page — so this is a real editorial decision and
 * it is made by dragging, which is how everybody expects to make it.
 *
 * **Dragged with the browser's own API, not a library.** A reorder is one `dragstart`, one
 * `dragover` and one `drop`; a drag-and-drop dependency is forty kilobytes and a second set of
 * behaviours to learn. The keyboard gets the same job done with two buttons per tile, because a
 * drag is not reachable without a mouse.
 *
 * **The server is told the whole order, not a pair of indexes.** A reorder is one intention.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { Alert, Button } from "@/components/ui";
import { IconClose, IconDrag, IconPlus } from "@/components/icons";

export interface Photo {
  id: string;
  name: string;
  width: number | null;
  height: number | null;
  position: number;
}

/** Served by the API after it has looked the row up — never a path built here. */
export const photoSrc = (id: string) => `/api/v1/public/media/${id}`;

const MAX = 10;

/**
 * The upload itself, on its own so both callers can use it.
 *
 * `api.post` sends JSON; a photograph is multipart, and base64 in a JSON body would make a five
 * megabyte picture a third bigger on the connection that is already the slow part. Returns the
 * problem to show, or null when everything landed.
 */
export async function uploadUnitPhotos(unitId: string, files: File[]): Promise<string | null> {
  if (!files.length) return null;

  const form = new FormData();
  for (const file of files) form.append("photos", file);

  try {
    const response = await fetch(`/api/v1/units/${unitId}/photos`, {
      method: "POST",
      body: form,
      credentials: "include",
    });
    if (response.ok) return null;
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    return payload?.error ?? "Those photographs did not upload.";
  } catch {
    return "Could not reach the server. Check your connection and try again.";
  }
}

/**
 * Photographs chosen before the unit exists.
 *
 * A photograph is attached to a unit id, so it cannot be uploaded until there is one — but that is
 * the server's problem, not the agency's. Nobody should have to save a form, find their way back
 * to it and start again with the file picker. The files are held here, shown from the browser's
 * own copy, and posted the moment the unit has an id.
 *
 * The same grid, the same order-by-dragging and the same first-one-is-the-main-one rule as the
 * saved version, so what somebody learns here still applies afterwards.
 */
export function PendingUnitPhotos({
  files,
  onChange,
}: {
  files: File[];
  onChange: (next: File[]) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState<number | null>(null);

  /* Revoked when the tile goes, so a long session picking and unpicking does not leak. */
  const previews = useMemo(() => files.map((file) => ({ file, url: URL.createObjectURL(file) })), [files]);
  useEffect(() => () => previews.forEach((entry) => URL.revokeObjectURL(entry.url)), [previews]);

  const add = (picked: FileList | null) => {
    if (!picked?.length) return;
    onChange([...files, ...Array.from(picked)].slice(0, MAX));
    if (fileInput.current) fileInput.current.value = "";
  };

  const moveTo = (from: number, to: number) => {
    const next = [...files];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div>
      {/* As many across as the strip has room for, which is not the same as how wide the window
          is. On the unit form this sits in a 24rem column on the very screens `lg:grid-cols-4`
          was written for, and four tiles across that is 86px each with the label hanging out of
          the dashed box. */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(7.5rem,1fr))] gap-3">
        {previews.map((entry, index) => (
          <figure
            key={entry.url}
            draggable
            onDragStart={() => setDragging(index)}
            onDragEnd={() => setDragging(null)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              if (dragging !== null && dragging !== index) moveTo(dragging, index);
              setDragging(null);
            }}
            className={`group relative overflow-hidden rounded-[var(--radius-surface)] border bg-ink-50 ${
              dragging === index ? "border-brand-500 opacity-50" : "border-ink-200"
            }`}
          >
            <img
              src={entry.url}
              alt={entry.file.name}
              className="aspect-[4/3] w-full cursor-grab object-cover active:cursor-grabbing"
              draggable={false}
            />

            {index === 0 ? (
              <figcaption className="absolute left-2 top-2 rounded-[var(--radius-control)] bg-brand-600 px-2 py-0.5 text-[11px] font-semibold text-white">
                Main photo
              </figcaption>
            ) : null}

            <button
              type="button"
              aria-label={`Remove ${entry.file.name}`}
              onClick={() => onChange(files.filter((_, at) => at !== index))}
              className="absolute right-2 top-2 grid size-7 place-items-center rounded-full bg-white/90 text-ink-600 hover:bg-danger-600 hover:text-white"
            >
              <IconClose size={14} />
            </button>

            <span className="absolute inset-x-2 bottom-2 flex items-center gap-1">
              <span className="grid size-7 place-items-center rounded-[var(--radius-control)] bg-white/90 text-ink-400">
                <IconDrag size={14} />
              </span>
              <button
                type="button"
                aria-label="Move earlier"
                disabled={index === 0}
                onClick={() => moveTo(index, index - 1)}
                className="grid size-7 place-items-center rounded-[var(--radius-control)] bg-white/90 text-ink-600 disabled:opacity-40 hover:bg-white"
              >
                ←
              </button>
              <button
                type="button"
                aria-label="Move later"
                disabled={index === files.length - 1}
                onClick={() => moveTo(index, index + 1)}
                className="grid size-7 place-items-center rounded-[var(--radius-control)] bg-white/90 text-ink-600 disabled:opacity-40 hover:bg-white"
              >
                →
              </button>
            </span>
          </figure>
        ))}

        {files.length < MAX ? (
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            className="flex aspect-[4/3] flex-col items-center justify-center gap-1 rounded-[var(--radius-surface)] border border-dashed border-ink-300 text-sm text-ink-500 hover:border-brand-500 hover:bg-brand-50 hover:text-brand-700"
          >
            <IconPlus size={20} />
            Add photographs
          </button>
        ) : null}
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        multiple
        hidden
        onChange={(event) => add(event.target.files)}
      />

      <p className="mt-2 text-xs text-ink-500">
        {files.length
          ? `Drag to reorder — the first is what people see on the listing. ${files.length} of ${MAX}. They upload when you save.`
          : `Up to ${MAX}. JPEG, PNG or WebP, 8MB each. They upload when you save; until one is added the listing shows a drawing of this kind of building.`}
      </p>
    </div>
  );
}

export function UnitPhotos({ unitId }: { unitId: string }) {
  const client = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["unit-photos", unitId],
    queryFn: () => api.get<{ photos: Photo[] }>(`/units/${unitId}/photos`),
  });

  const photos = data?.photos ?? [];

  const reorder = useApiMutation({
    mutationFn: (ids: string[]) => api.patch(`/units/${unitId}/photos`, { ids }),
    invalidates: [["unit-photos", unitId]],
  });

  const remove = useApiMutation({
    mutationFn: (id: string) => api.delete(`/photos/${id}`),
    invalidates: [["unit-photos", unitId]],
  });

  /**
   * Uploading, by hand rather than through the shared client.
   *
   * `api.post` sends JSON; a photograph is multipart. Base64 in a JSON body would make a five
   * megabyte picture a third bigger on the connection that is already the slow part.
   */
  const [uploading, setUploading] = useState(false);

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    setProblem(null);
    setUploading(true);

    const failed = await uploadUnitPhotos(unitId, Array.from(files));
    if (failed) setProblem(failed);
    else await client.invalidateQueries({ queryKey: ["unit-photos", unitId] });

    setUploading(false);
    if (fileInput.current) fileInput.current.value = "";
  }

  /** Moves one photograph to where another one is, and tells the server the whole new order. */
  const moveTo = (id: string, index: number) => {
    const without = photos.filter((photo) => photo.id !== id).map((photo) => photo.id);
    without.splice(index, 0, id);
    reorder.mutate(without);
  };

  const move = (id: string, by: number) => {
    const from = photos.findIndex((photo) => photo.id === id);
    const to = Math.min(Math.max(from + by, 0), photos.length - 1);
    if (from !== to) moveTo(id, to);
  };

  return (
    <div>
      {problem ? (
        <div className="mb-3">
          <Alert tone="danger">{problem}</Alert>
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-ink-500">Loading photographs…</p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(7.5rem,1fr))] gap-3">
          {photos.map((photo, index) => (
            <figure
              key={photo.id}
              draggable
              onDragStart={() => setDragging(photo.id)}
              onDragEnd={() => setDragging(null)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                if (dragging && dragging !== photo.id) moveTo(dragging, index);
                setDragging(null);
              }}
              className={`group relative overflow-hidden rounded-[var(--radius-surface)] border bg-ink-50 ${
                dragging === photo.id ? "border-brand-500 opacity-50" : "border-ink-200"
              }`}
            >
              <img
                src={photoSrc(photo.id)}
                alt={photo.name}
                className="aspect-[4/3] w-full cursor-grab object-cover active:cursor-grabbing"
                draggable={false}
              />

              {/* The first one is not just first in a list — it is the picture everything else
                  shows. Saying so is what makes the drag feel worth doing. */}
              {index === 0 ? (
                <figcaption className="absolute left-2 top-2 rounded-[var(--radius-control)] bg-brand-600 px-2 py-0.5 text-[11px] font-semibold text-white">
                  Main photo
                </figcaption>
              ) : null}

              <span className="absolute right-2 top-2 flex gap-1">
                <button
                  type="button"
                  aria-label={`Remove ${photo.name}`}
                  onClick={() => remove.mutate(photo.id)}
                  disabled={remove.isPending}
                  className="grid size-7 place-items-center rounded-full bg-white/90 text-ink-600 hover:bg-danger-600 hover:text-white"
                >
                  <IconClose size={14} />
                </button>
              </span>

              {/* A drag is not reachable without a mouse; these are. */}
              <span className="absolute inset-x-2 bottom-2 flex items-center gap-1">
                <span className="grid size-7 place-items-center rounded-[var(--radius-control)] bg-white/90 text-ink-400">
                  <IconDrag size={14} />
                </span>
                <button
                  type="button"
                  aria-label="Move earlier"
                  disabled={index === 0 || reorder.isPending}
                  onClick={() => move(photo.id, -1)}
                  className="grid size-7 place-items-center rounded-[var(--radius-control)] bg-white/90 text-ink-600 disabled:opacity-40 hover:bg-white"
                >
                  ←
                </button>
                <button
                  type="button"
                  aria-label="Move later"
                  disabled={index === photos.length - 1 || reorder.isPending}
                  onClick={() => move(photo.id, 1)}
                  className="grid size-7 place-items-center rounded-[var(--radius-control)] bg-white/90 text-ink-600 disabled:opacity-40 hover:bg-white"
                >
                  →
                </button>
              </span>
            </figure>
          ))}

          {photos.length < MAX ? (
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="flex aspect-[4/3] flex-col items-center justify-center gap-1 rounded-[var(--radius-surface)] border border-dashed border-ink-300 text-sm text-ink-500 hover:border-brand-500 hover:bg-brand-50 hover:text-brand-700"
            >
              <IconPlus size={20} />
              {uploading ? "Uploading…" : "Add photographs"}
            </button>
          ) : null}
        </div>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        multiple
        hidden
        onChange={(event) => upload(event.target.files)}
      />

      <p className="mt-2 text-xs text-ink-500">
        {photos.length
          ? `Drag to reorder — the first is what people see on the listing. ${photos.length} of ${MAX}.`
          : `Up to ${MAX}. JPEG, PNG or WebP, 8MB each. Until one is added, the listing shows a drawing of this kind of building.`}
      </p>

      {photos.length ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-2"
          onClick={() => fileInput.current?.click()}
          disabled={uploading || photos.length >= MAX}
        >
          <IconPlus size={15} />
          Add more
        </Button>
      ) : null}
    </div>
  );
}
